import { Loader2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/components/shared";
import { getApiErrorMessage } from "@/lib";
import { type AvailableIntegration, integrationsService } from "@/lib/services";
import { IntegrationRow } from "./IntegrationRow";

export function SettingsIntegrationsTab() {
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [integrations, setIntegrations] = useState<AvailableIntegration[]>([]);
  const [loading, setLoading] = useState(true);
  const [busySlug, setBusySlug] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setIntegrations(await integrationsService.listAvailable());
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to load integrations"), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
  }, [load]);

  // Surface the OAuth callback result (?connected=<slug>&status=success|error).
  useEffect(() => {
    const connected = searchParams.get("connected");
    const status = searchParams.get("status");
    if (!connected || !status) return;
    if (status === "success") {
      showToast("Connected", "success");
    } else {
      showToast("Failed to connect", "error");
    }
    searchParams.delete("connected");
    searchParams.delete("status");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, showToast]);

  const handleConnect = async (slug: string) => {
    try {
      setBusySlug(slug);
      const { authorizeUrl } = await integrationsService.getAuthorizeUrl(slug);
      window.location.href = authorizeUrl;
    } catch (err) {
      setBusySlug(null);
      showToast(getApiErrorMessage(err, "Could not start the connection"), "error");
    }
  };

  const handleDisconnect = async (slug: string) => {
    try {
      setBusySlug(slug);
      await integrationsService.disconnect(slug);
      showToast("Disconnected", "success");
      await load();
    } catch (err) {
      showToast(getApiErrorMessage(err, "Failed to disconnect"), "error");
    } finally {
      setBusySlug(null);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-content-primary">Integrations</h2>
        <p className="text-sm text-content-tertiary mt-0.5">
          Connect your own accounts so the assistant can act on your behalf in these tools.
        </p>
      </div>
      <IntegrationsList
        loading={loading}
        integrations={integrations}
        busySlug={busySlug}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
      />
    </div>
  );
}

interface IntegrationsListProps {
  loading: boolean;
  integrations: AvailableIntegration[];
  busySlug: string | null;
  onConnect: (slug: string) => void;
  onDisconnect: (slug: string) => void;
}

function IntegrationsList({ loading, integrations, busySlug, onConnect, onDisconnect }: IntegrationsListProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (integrations.length === 0) {
    return (
      <p className="text-sm text-content-tertiary py-6">
        No integrations are available. An administrator can enable integrations that support per-user connections.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {integrations.map((it) => (
        <IntegrationRow
          key={it.slug}
          integration={it}
          busy={busySlug === it.slug}
          onConnect={onConnect}
          onDisconnect={onDisconnect}
        />
      ))}
    </div>
  );
}
